import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { Feedback } from './feedbacks-reducer';
import type { LeaveFeedbackItemViewModel } from 'tradera/apps/my-tradera/app/components/leave-feedback/types';
import qs from 'qs';
import type {
    GetBuyerPurchaseOrderForLeaveFeedbackApiResponse,
    GetBuyerPurchaseOrdersForLeaveFeedbackApiResponse,
    GetSellerPurchaseOrderForLeaveFeedbackApiResponse,
    GetSellerPurchaseOrdersForLeaveFeedbackApiResponse,
} from 'tradera/state/services/types/webapi-mytradera-generated';

const leaveFeedbackApi = createApi({
    reducerPath: 'leaveFeedbackApi',
    tagTypes: ['PagedFeedback'],
    baseQuery: fetchBaseQuery({
        baseUrl: '',
    }),
    endpoints: (builder) => ({
        getFeedbackItems: builder.query<
            { items: LeaveFeedbackItemViewModel[] },
            { role: string; transactionIds: string[] }
        >({
            query: ({ role, transactionIds }) => ({
                url: `/my/leave-feedback/${role}/items?${qs.stringify({
                    ids: transactionIds,
                })}`,
                method: 'GET',
            }),
        }),
        getFeedbackItemsAsSellerForPurchaseOrder: builder.query<
            GetSellerPurchaseOrderForLeaveFeedbackApiResponse,
            { purchaseOrderId: string }
        >({
            query: ({ purchaseOrderId }) => ({
                url: `/api/webapi/mytradera/leave-feedback-as-seller/purchase-order?${qs.stringify(
                    {
                        purchaseOrderId: purchaseOrderId,
                    },
                )}`,
            }),
        }),
        getFeedbackItemsAsBuyerForPurchaseOrder: builder.query<
            GetBuyerPurchaseOrderForLeaveFeedbackApiResponse,
            { purchaseOrderId: string }
        >({
            query: ({ purchaseOrderId }) => ({
                url: `/api/webapi/mytradera/leave-feedback-as-buyer/purchase-order?${qs.stringify(
                    {
                        purchaseOrderId: purchaseOrderId,
                    },
                )}`,
            }),
        }),
        getSellerTransactionsForLeaveFeedback: builder.query<
            GetSellerPurchaseOrdersForLeaveFeedbackApiResponse,
            { skip?: number; take?: number }
        >({
            providesTags: ['PagedFeedback'],
            query: ({ skip = 0, take = 10 }) =>
                `/api/webapi/mytradera/leave-feedback-as-seller/purchase-orders?${qs.stringify(
                    {
                        skip,
                        take,
                    },
                )}`,
        }),
        getBuyerTransactionsForLeaveFeedback: builder.query<
            GetBuyerPurchaseOrdersForLeaveFeedbackApiResponse,
            { skip?: number; take?: number }
        >({
            providesTags: ['PagedFeedback'],
            query: ({ skip = 0, take = 10 }) =>
                `/api/webapi/mytradera/leave-feedback-as-buyer/purchase-orders?${qs.stringify(
                    {
                        skip,
                        take,
                    },
                )}`,
        }),
        submitFeedback: builder.mutation<
            {
                failures?: {
                    errorCode: string;
                    transactionId: number;
                }[];
            },
            {
                role: string;
                data: Feedback[];
            }
        >({
            query: ({ role, data }) => ({
                url: `/my/leave-feedback/${role}/save`,
                method: 'POST',
                body: data,
            }),
            // this is an ugly hack to invalidate the paged feedback query after the mutation is successful
            // this is because data is updated too slow in BE so we need to wait some time before requesting a new list
            async onQueryStarted(props, { dispatch, queryFulfilled }) {
                await queryFulfilled;
                setTimeout(() => {
                    dispatch(
                        leaveFeedbackApi.util.invalidateTags(['PagedFeedback']),
                    );
                }, 1000);
            },
        }),
    }),
});

export const {
    useGetFeedbackItemsQuery,
    useSubmitFeedbackMutation,
    useGetFeedbackItemsAsSellerForPurchaseOrderQuery,
    useGetFeedbackItemsAsBuyerForPurchaseOrderQuery,
    useGetSellerTransactionsForLeaveFeedbackQuery,
    useGetBuyerTransactionsForLeaveFeedbackQuery,
} = leaveFeedbackApi;

export default leaveFeedbackApi;
